import { useTranslation } from 'react-i18next';

import { CollectionGuide, useCollectionCopy } from '@todo/web-ui/components/collection-guide';
import { EmptyState } from '@todo/web-ui/components/empty-state';
import { GuideCard } from '@todo/web-ui/components/guide-card';
import { Intro, Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { CardGrid, Section } from '@todo/web-ui/components/section';
import { seedScript } from '@todo/web-ui/components/seed-script';
import { GUIDES_PATH, guidePath, guidesCrumb } from '@todo/web-ui/lib/guides';
import { useArticles } from '@todo/core/hooks/use-articles';
import { useLocale } from '@todo/core/i18n/use-locale';
import { articlesIndexSeedKey } from '@todo/core/list-queries';
import { absoluteUrl, breadcrumbJsonLd, faqPageJsonLd, itemListJsonLd, seoTitle } from '@todo/core/seo';

// Every guide in the page's language, newest first — from the build's seed
// (@todo/web-ui/lib/guides); a dev server, which has no seeds, fetches them — then
// the planning guide and FAQ. Indexed once it lists one.
export default function GuidesIndex() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { articles, loading } = useArticles(locale);
  const url = absoluteUrl(locale, GUIDES_PATH);
  // The text under the cards — planning basics + FAQ for "Dubai travel guide"
  // searches — from `collections.guides` in the resources, like /boats.
  const copy = useCollectionCopy('guides');

  return (
    <Page>
      <PageHead
        title={seoTitle(t('guides.metaTitle'))}
        description={t('guides.metaDescription')}
        path={GUIDES_PATH}
        indexable={articles.length > 0}>
        <script type="application/ld+json">
          {JSON.stringify(
            itemListJsonLd(
              articles.map((a) => ({ name: a.title, url: absoluteUrl(locale, guidePath(a.slug)) })),
              url,
            ),
          )}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [{ name: guidesCrumb(locale).name }]))}
        </script>
        {copy.faq.length ? <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(copy.faq))}</script> : null}
        {seedScript(articlesIndexSeedKey(locale))}
      </PageHead>
      <Section id="guides-title" level={1} title={t('guides.title')}>
        <Intro>{t('guides.intro')}</Intro>
        {articles.length ? (
          <CardGrid>
            {articles.map((a) => (
              <GuideCard key={a._id} article={a} />
            ))}
          </CardGrid>
        ) : loading ? null : (
          <EmptyState text={t('guides.empty')} exclude={GUIDES_PATH} />
        )}
      </Section>

      <CollectionGuide slug="guides" copy={copy} />
    </Page>
  );
}
